import styled from '@emotion/styled';
import { useFormikContext } from 'formik';
import React, { useState } from 'react';
import Scrollspy from 'react-scrollspy';

import ScrollToElementLink from 'App/components/Assessments/ScrollToElementLink';
import DeleteModal from 'App/components/DeleteModal';
import ClickLink from 'App/components/styles/ClickLink';
import contentGroupPropType from '../types/contentGroupPropType';
import { contentTitles, scrollContainerId } from './constants';
import getGroupScrollId from './utils/getGroupScrollId';

const ScrollspyStyle = styled(Scrollspy)`
    background-color: var(--sideBarBackground);
    padding: 20px;
    flex-shrink: 0;
    overflow: auto;

    .btn {
        color: var(--brandDanger);
    }
`;

const ScrollspyItemStyle = styled.div`
    display: flex;
    align-items: center;
    margin: 1em 0;
    font-size: 0.75rem;
    color: var(--matisse);

    &.active {
        color: var(--biscay);
    }

    & a {
        color: inherit;

        &:focus {
            text-decoration: none;
        }
    }
`;

const ScrollspyItemIcon = styled.div`
    display: flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    background-color: var(--matisse);
    color: white;
    border-radius: 50%;
    width: 25px;
    height: 25px;
    margin-right: 5px;

    .active & {
        background-color: var(--biscay);
    }
`;

const ChildGroupHeaderStyle = styled.div`
    color: var(--storm);
    font-size: 18px;
    margin-top: 25px;
`;

const ContentLibraryScrollspy = ({ contentGroup }) => {
    const { values, setFieldValue } = useFormikContext();
    const [associationToDelete, setAssociationToDelete] = useState(null);

    return (
        <ScrollspyStyle
            items={[
                ...contentGroup.contentVersions.map(getGroupScrollId),
                ...Object.values(values.children)
                    .filter((child) => !child.deleted)
                    .flatMap((child) => [child.uuid, ...Object.values(child.contentVersions).map(getGroupScrollId)]),
            ]}
            currentClassName="active"
            componentTag="div"
            rootEl={`#${scrollContainerId}`}
        >
            {contentGroup.contentVersions.map((contentVersion, index) => (
                <ScrollspyItemStyle key={contentVersion.uuid}>
                    <ScrollspyItemIcon>{index + 1}</ScrollspyItemIcon>
                    <ScrollToElementLink elementId={getGroupScrollId(contentVersion)}>
                        {contentTitles[contentGroup.type][contentVersion.label]}
                    </ScrollToElementLink>
                </ScrollspyItemStyle>
            ))}

            {Object.values(values.children)
                .filter((child) => !child.deleted)
                .map((child) => [
                    <div key={child.uuid}>
                        <ChildGroupHeaderStyle>{child.name.replace(contentGroup.name, '')}</ChildGroupHeaderStyle>
                        <ClickLink onClick={() => setAssociationToDelete(child.uuid)}>Delete Association</ClickLink>
                    </div>,
                    ...Object.values(child.contentVersions).map((contentVersion, index) => (
                        <ScrollspyItemStyle key={contentVersion.uuid}>
                            <ScrollspyItemIcon>{index + 1}</ScrollspyItemIcon>
                            <ScrollToElementLink elementId={getGroupScrollId(contentVersion)}>
                                {contentTitles[contentGroup.type][contentVersion.label]}
                            </ScrollToElementLink>
                        </ScrollspyItemStyle>
                    )),
                ])}

            {associationToDelete && (
                <DeleteModal
                    show
                    title="Delete Association"
                    message="Are you sure you want to delete this association?"
                    onHide={() => setAssociationToDelete(null)}
                    onDelete={() => {
                        setFieldValue(`children.${associationToDelete}.deleted`, true);
                        setAssociationToDelete(null);
                    }}
                />
            )}
        </ScrollspyStyle>
    );
};

ContentLibraryScrollspy.propTypes = {
    contentGroup: contentGroupPropType.isRequired,
};

export default ContentLibraryScrollspy;
